import { forwardRef, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

// utils
import { cn } from "@/lib/utils";
import { twMerge } from "tailwind-merge";

export type variant = "filled" | "outlined" | "ghost";
export type color =
    | "primary"
    | "secondary"
    | "tertiary"
    | "destructive"
    | "neutral";
export type size = "sm" | "md" | "lg";
export type className = string;
export type children = ReactNode;
export type loading = boolean;

export interface IconButtonProps extends React.ComponentProps<"button"> {
    variant?: variant;
    onClick?: () => void;
    size?: size;
    color?: color;
    loading?: loading;
    disabled?: boolean;
    className?: className;
    children: children;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            variant,
            size,
            color,
            disabled,
            loading,
            className,
            children,
            onClick,
        },
        ref
    ) => {
        // 1. init

        // 2. set default props
        variant = variant ?? "filled";
        size = size ?? "md";

        // 4. set styles
        const buttonBase =
            "relative inline-flex items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 cursor-pointer flex-shrink-0";

        const buttonVariant = {
            filled: {
                primary:
                    "bg-md-primary text-md-on-primary hover:bg-md-primary/90",
                secondary:
                    "bg-md-secondary-container text-md-on-secondary-container hover:bg-md-secondary-container/90",
                tertiary:
                    "bg-md-tertiary-container text-md-on-tertiary-container hover:bg-md-tertiary-container/90",
                destructive:
                    "bg-md-error text-md-on-error hover:bg-md-error/90",
                neutral:
                    "bg-md-surface-variant text-md-on-surface-variant hover:bg-md-surface-variant/90",
            },
            outlined: {
                primary:
                    "border border-md-outline text-md-primary hover:bg-md-primary/8",
                secondary:
                    "border border-md-outline text-md-on-secondary-container hover:bg-md-secondary-container/8",
                tertiary:
                    "border border-md-outline text-md-on-tertiary-container hover:bg-md-tertiary-container/8",
                destructive:
                    "border border-md-outline text-md-error hover:bg-md-error/8",
                neutral:
                    "border border-md-outline text-md-on-surface hover:bg-md-surface/8",
            },
            ghost: {
                primary: "text-md-primary hover:bg-md-primary/8",
                secondary:
                    "text-md-on-secondary-container hover:bg-md-secondary-container/8",
                tertiary:
                    "text-md-on-tertiary-container hover:bg-md-tertiary-container/8",
                destructive: "text-md-error hover:bg-md-error/8",
                neutral:
                    "text-md-on-surface-variant hover:bg-md-surface-variant",
            },
        }[variant][color || "primary"];

        const buttonSize = {
            sm: "h-8 w-8 [&>*]:h-4 [&>*]:w-4",
            md: "h-10 w-10 [&>*]:h-5 [&>*]:w-5",
            lg: "h-12 w-12 [&>*]:h-6 [&>*]:w-6",
        }[size];

        const classes = twMerge(
            cn(buttonBase, buttonSize, buttonVariant),
            className
        );

        // 5. return
        return (
            <button
                ref={ref}
                className={classes}
                disabled={disabled || loading}
                type={"button"}
                onClick={loading ? undefined : onClick}
            >
                {loading ? <Loader2 className="animate-spin" /> : children}
            </button>
        );
    }
);

IconButton.displayName = "IconButton";

export default IconButton;
